import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Unlock notification context
const UnlockNotificationContext = createContext(null);

// Celebratory confetti effect
const triggerConfetti = () => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio)
    });
  }

  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

// Category-specific emojis and colors
const UNLOCK_CONFIG = {
  art_styles: {
    emoji: '🎨',
    label: 'Art Style',
    color: 'from-purple-500 to-pink-500'
  },
  genres: {
    emoji: '📚',
    label: 'Genre',
    color: 'from-blue-500 to-indigo-500'
  },
  features: {
    emoji: '✨',
    label: 'Feature',
    color: 'from-amber-500 to-orange-500'
  }
};

// Custom toast component for unlock notifications
const UnlockToast = ({ unlock, onClose }) => {
  const config = UNLOCK_CONFIG[unlock.category] || UNLOCK_CONFIG.features;
  
  return (
    <div className="flex items-center gap-3 p-1">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center text-2xl shadow-lg`}>
        {config.emoji}
      </div>
      <div className="flex-1">
        <p className="font-bold text-gray-900">
          {unlock.name} Unlocked!
        </p>
        <p className="text-sm text-gray-500">
          New {config.label.toLowerCase()} available
        </p>
      </div>
    </div>
  );
};

// Tier up toast component
const TierUpToast = ({ tier }) => (
  <div className="flex items-center gap-3 p-1">
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-2xl shadow-lg animate-bounce">
      👑
    </div>
    <div className="flex-1">
      <p className="font-bold text-gray-900">
        Tier Up! {tier.name}
      </p>
      <p className="text-sm text-gray-500">
        You reached a new achievement tier!
      </p>
    </div>
  </div>
);

export const UnlockNotificationProvider = ({ children }) => {
  const [pendingUnlocks, setPendingUnlocks] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const lastCheckRef = useRef(0);
  const checkIntervalRef = useRef(null);

  // Check for new unlocks
  const checkForNewUnlocks = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Throttle checks to once every 5 seconds unless forced
    const now = Date.now();
    if (!force && now - lastCheckRef.current < 5000) return;
    lastCheckRef.current = now;
    
    if (isChecking) return;
    setIsChecking(true);
    
    try {
      const response = await fetch(`${API_URL}/api/unlockables/check-new`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) return;
      
      const data = await response.json();
      
      // Show tier up notification first
      if (data.tier_up && data.new_tier) {
        triggerConfetti();
        toast.custom(
          (t) => <TierUpToast tier={data.new_tier} />,
          {
            duration: 6000,
            position: 'top-center'
          }
        );
      }
      
      // Show unlock notifications with staggered timing
      if (data.new_unlocks && data.new_unlocks.length > 0) {
        // Trigger confetti for unlocks (if no tier up already triggered it)
        if (!data.tier_up) {
          triggerConfetti();
        }
        
        data.new_unlocks.forEach((unlock, index) => {
          setTimeout(() => {
            toast.custom(
              (t) => <UnlockToast unlock={unlock} onClose={() => toast.dismiss(t)} />,
              {
                duration: 5000,
                position: 'top-center'
              }
            );
          }, index * 800); // Stagger notifications by 800ms
        });
        
        setPendingUnlocks(prev => [...prev, ...data.new_unlocks]);
      }
    } catch (err) {
      console.error('Failed to check for new unlocks:', err);
    } finally {
      setIsChecking(false);
    }
  }, [isChecking]);

  // Mark all unlocks as seen
  const markUnlocksSeen = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/unlockables/mark-seen`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setPendingUnlocks([]);
    } catch (err) {
      console.error('Failed to mark unlocks as seen:', err);
    }
  }, []);

  // Trigger check on specific user actions
  const triggerUnlockCheck = useCallback(() => {
    // Small delay to allow backend to process the action
    setTimeout(() => checkForNewUnlocks(true), 1000);
  }, [checkForNewUnlocks]);

  // Set up periodic check when user is active
  useEffect(() => {
    // Check on mount
    checkForNewUnlocks();
    
    // Set up interval for periodic checks (every 30 seconds)
    checkIntervalRef.current = setInterval(() => {
      checkForNewUnlocks();
    }, 30000);
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkForNewUnlocks]);

  const value = {
    pendingUnlocks,
    isChecking,
    checkForNewUnlocks,
    triggerUnlockCheck,
    markUnlocksSeen
  };

  return (
    <UnlockNotificationContext.Provider value={value}>
      {children}
    </UnlockNotificationContext.Provider>
  );
};

// Hook to use unlock notifications
export const useUnlockNotifications = () => {
  const context = useContext(UnlockNotificationContext);
  if (!context) {
    throw new Error('useUnlockNotifications must be used within UnlockNotificationProvider');
  }
  return context;
};

export default UnlockNotificationProvider;
